import { FunctionComponent, useEffect, useRef, useState } from "react"
import { Button, Col, Row, Table } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate } from "react-router-dom"
import FilterField from "../components/FilterField"
import RecalculateKeycardDoorsButton from "../components/RecalculateKeycardDoorsButton"
import { AccessController } from "../data/models.interface"
import { useGetAccessControllersQuery } from "../hooks/queries/useGetAccessControllersQuery"
import useSessionStorage from "../hooks/useSessionStorage"
import useSort from "../hooks/useSort"
import { filterProperty } from "../utils/StringUtils"

interface IAccessControllersPage {}
const AccessControllersPage: FunctionComponent<IAccessControllersPage> = () => {
	const navigate = useNavigate()
	//query Access Controllers
	const accessControllersQuery = useGetAccessControllersQuery()
	let accessControllers = accessControllersQuery.data?.accessControllers
	//allow the user to auto-refresh the page
	const [autoRefresh, setAutoRefresh] = useState(false)
	//keep track of how many times the data has refreshed with no change
	const [refreshesWithNoChange, setRefreshesWithNoChange] = useState(0)
	//if the data does change, reset that counter
	const [previousAccessControllers, setPreviousAccessControllers] = useState<AccessController[]>([])
	useEffect(() => {
		if (autoRefresh && accessControllers) {
			if (JSON.stringify(accessControllers) !== JSON.stringify(previousAccessControllers)) {
				console.log("Auto-refresh found new data")
				setPreviousAccessControllers(accessControllers)
				setRefreshesWithNoChange(0)
			}
		}
	}, [autoRefresh, accessControllers, previousAccessControllers])
	//when auto refresh is active, refresh the data periodically and increment the counter
	const timerRef = useRef<NodeJS.Timeout | null>(null)
	const query = accessControllersQuery.query
	useEffect(() => {
		if (autoRefresh) {
			timerRef.current = setInterval(() => {
				setRefreshesWithNoChange((prev) => prev + 1)
				console.log("Auto-refresh Access Controllers query")
				query()
			}, 10 * 1000)
		} else if (timerRef.current) {
			clearInterval(timerRef.current)
			timerRef.current = null
		}
		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current)
				timerRef.current = null
			}
		}
	}, [autoRefresh, query])
	//if the counter goes too high, disable auto-refresh
	useEffect(() => {
		if (refreshesWithNoChange > 6) {
			console.log("Disabling auto-refresh because nothing is changing")
			setAutoRefresh(false)
			setRefreshesWithNoChange(0)
		}
	}, [refreshesWithNoChange])
	//if autoRefresh is on for too long, disable it
	useEffect(() => {
		let autoRefreshLimitTimer: NodeJS.Timeout | null = null
		if (autoRefresh) {
			autoRefreshLimitTimer = setTimeout(
				() => {
					console.log("Disabling auto-refresh because it ran too long")
					setAutoRefresh(false)
					setRefreshesWithNoChange(0)
				},
				20 * 60 * 1000,
			)
		}
		return () => {
			if (autoRefreshLimitTimer) clearTimeout(autoRefreshLimitTimer)
		}
	}, [autoRefresh])
	//filters for the table
	const [serialFilter, setSerialFilter] = useSessionStorage("access-controllers-serialNumber", "")
	const [propertyNameFilter, setPropertyNameFilter] = useSessionStorage("access-controllers-propertyName", "")
	const [ipAddressFilter, setIpaddressFilter] = useSessionStorage("access-controllers-ipAddress", "")
	const [activeFilter, setActiveFilter] = useSessionStorage("access-controllers-active", "")
	const [changesFilter, setChangesFilter] = useSessionStorage("access-controllers-pendingChanges", "")
	const [updateFilter, setUpdateFilter] = useSessionStorage("access-controllers-update", "")
	const { sortBy, sort } = useSort<AccessController>("serialNumber")
	//loading layout
	if (!accessControllers) return <LoadingLayout />
	//apply the filters
	accessControllers = filterProperty(accessControllers, (c) => c.serialNumber, serialFilter)
	accessControllers = filterProperty(accessControllers, (c) => c.propertyName, propertyNameFilter)
	accessControllers = filterProperty(accessControllers, (c) => c.ipAddress + ": " + c.port, ipAddressFilter)
	accessControllers = filterProperty(accessControllers, (c) => (c.active ? "Enabled" : "Disabled"), activeFilter)
	accessControllers = filterProperty(accessControllers, (c) => c.status.pendingChanges + "", changesFilter)
	accessControllers = filterProperty(accessControllers, (c) => (c.status.updateInProgress ? "True" : ""), updateFilter)
	accessControllers = sort(accessControllers)
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Access Controllers</h2>
			<Row className="mb-2">
				<Col />
				<Col xs="auto">
					<RecalculateKeycardDoorsButton
						setActiveFilter={setActiveFilter}
						sortBy={sortBy}
						setAutoRefresh={setAutoRefresh}
					/>
				</Col>
				<Col xs="auto" className="d-flex align-items-center justify-content-end">
					<div className="form-check">
						<input
							className="form-check-input"
							type="checkbox"
							id="autoRefresh"
							checked={autoRefresh}
							onChange={(e) => setAutoRefresh(e.target.checked)}
						/>
						<label className="form-check-label" htmlFor="autoRefresh">
							Auto-Refresh
						</label>
					</div>
				</Col>
			</Row>
			<Table striped bordered hover size="sm">
				<thead>
					<tr>
						<th onClick={() => sortBy("serialNumber")}>Serial Number</th>
						<th onClick={() => sortBy("propertyName")}>Property</th>
						<th onClick={() => sortBy("ipAddress")}>IP Address</th>
						<th onClick={() => sortBy("active")}>Active</th>
						<th>Online</th>
						<th onClick={() => sortBy("status.pendingChanges")}>Pending Changes</th>
						<th onClick={() => sortBy("status.updateInProgress")}>Update in Progress</th>
					</tr>
					<tr>
						<td>
							<FilterField filter={serialFilter} setFilter={setSerialFilter} placeholder="Filter Serial Number" />
						</td>
						<td>
							<FilterField
								filter={propertyNameFilter}
								setFilter={setPropertyNameFilter}
								placeholder="Filter Property"
							/>
						</td>
						<td>
							<FilterField filter={ipAddressFilter} setFilter={setIpaddressFilter} placeholder="Filter IP Address" />
						</td>
						<td>
							<FilterField filter={activeFilter} setFilter={setActiveFilter} placeholder="Filter Active" />
						</td>
						<td></td>
						<td>
							<FilterField filter={changesFilter} setFilter={setChangesFilter} placeholder="Filter Pending Changes" />
						</td>
						<td>
							<FilterField filter={updateFilter} setFilter={setUpdateFilter} placeholder="Filter Update in Progress" />
						</td>
					</tr>
				</thead>
				<tbody>
					{accessControllers.map((controller) => {
						return (
							<tr key={controller.id} onClick={() => navigate(`/access-controllers/${controller.id}`)}>
								<td>{controller.serialNumber}</td>
								<td>{controller.propertyName}</td>
								<td>
									{controller.ipAddress}: {controller.port}
								</td>
								<td>{controller.active ? "Enabled" : "Disabled"}</td>
								<td>
									{controller.status.online
										? "Online"
										: controller.status.lastSeen
											? "Last Seen " + controller.status.lastSeen
											: ""}
								</td>
								<td>{controller.status.pendingChanges}</td>
								<td>{controller.status.updateInProgress ? "True" : ""}</td>
							</tr>
						)
					})}
				</tbody>
			</Table>
			<Row>
				<Col xs="auto">
					<BackButton />
				</Col>
				<Col />
				<Col xs="auto">
					<Button variant="success" onClick={() => navigate("/access-controllers/new")}>
						New
					</Button>
				</Col>
			</Row>
		</Container>
	)
}
export default AccessControllersPage

const LoadingLayout = () => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Access Controllers</h2>
			<div className="text-center">Loading</div>
		</Container>
	)
}

const BackButton = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-3" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}
